import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from './Toast';
import { CartContext } from '../context/CartContext';
import { useWishlistContext } from '../context/WishlistContext';
import AuthContext from '../context/AuthContext';
import AccessoriesModal from './AccessoriesModal';
import { getCartRecommendations } from '../api/product.api';
import { resolveAssetUrl, productPath } from '../config';
import { getDisplayPrices, shouldShowIncVatLabel } from '../utils/pricing';

const getImageUrl = (imagePath) => resolveAssetUrl(imagePath);

export default function ProductCard({ product }) {
    const navigate = useNavigate();
    const { addToCart } = useContext(CartContext);
    const { isAuthenticated } = useContext(AuthContext);
    const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlistContext();
    const [adding, setAdding] = useState(false);
    const [wishlistLoading, setWishlistLoading] = useState(false);
    const [accessoryItems, setAccessoryItems] = useState([]);
    const [showAccessoriesModal, setShowAccessoriesModal] = useState(false);

    const productId = product._id || product.id;

    const pricing = getDisplayPrices(product);
    const price = pricing.displayPrice;
    const strikePrice = pricing.strikePrice;
    const hasOffer = pricing.hasOffer;
    const discount = product.discount ?? product.discountPercentage ?? (
        strikePrice && strikePrice > price
            ? Math.round(((strikePrice - price) / strikePrice) * 100)
            : 0
    );
    const productLink = productPath(
        product.variantSlug
        || product.variant?.slug
        || product.variants?.find?.((v) => v?.slug)?.slug
        || product.variants?.[0]?.slug
        || product.slug
        || product._id
        || product.id
    );
    const isOutOfStock =
        typeof product.inStock === "boolean"
            ? !product.inStock
            : Boolean(product.forceOutOfStock);

    // Smart variant selection for cart and wishlist
    // Prefer availableVariantId (first variant with stock), then fallback to default
    const selectedVariantId = product.availableVariantId || product.defaultVariantId || product.variantId;
    const isWishlisted = selectedVariantId ? isInWishlist(selectedVariantId) : false;

    const handleCardClick = () => {
        navigate(productLink);
    };

    const handleWishlistClick = async (e) => {
        e.stopPropagation();
        if (!isAuthenticated) {
            toast.info('Please login to add items to your wishlist');
            navigate('/login');
            return;
        }
        if (!selectedVariantId) {
            toast.info('Please open the product to select a variant first');
            return;
        }
        setWishlistLoading(true);
        try {
            if (isWishlisted) {
                await removeFromWishlist(selectedVariantId);
                toast.success('Removed from wishlist');
            } else {
                await addToWishlist(selectedVariantId);
                toast.success('Added to wishlist!');
            }
        } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to update wishlist';
            toast.error(msg);
        } finally {
            setWishlistLoading(false);
        }
    };

    const handleAddToCart = async (e) => {
        e.stopPropagation();

        // Smart variant selection:
        // 1. Try availableVariantId (first variant with stock)
        // 2. Fall back to defaultVariantId
        // 3. Fall back to legacy variantId
        const variantId = product.availableVariantId || product.defaultVariantId || product.variantId;

        if (variantId) {
            try {
                setAdding(true);
                await addToCart(variantId, 1);
                toast.success(`${product.name} added to cart!`);

                // Surface compatible accessories for this product, if any.
                if (productId) {
                    try {
                        const res = await getCartRecommendations([productId], 24);
                        const items = res?.data?.isOk ? (res.data.data || []) : [];
                        if (items.length > 0) {
                            setAccessoryItems(items);
                            setShowAccessoriesModal(true);
                        }
                    } catch (recErr) {
                        console.error('Error fetching accessory recommendations:', recErr);
                    }
                }
            } catch (err) {
                const msg = err.response?.data?.message || 'Failed to add to cart';
                toast.error(msg);
            } finally {
                setAdding(false);
            }
        } else {
            // No variant info available — navigate to detail page for selection
            navigate(productLink);
        }
    };

    return (
        <div
            className="bg-card rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-border flex flex-col h-full group"
            onClick={handleCardClick}
        >
            {/* Image Section */}
            <div className="relative p-4 product-image-well rounded-t-lg">
                <img
                    src={getImageUrl(product.image)}
                    alt={product.name}
                    className="w-full h-48 object-contain"
                />

                {/* Discount Badge */}
                {discount > 0 && (
                    <div className="absolute top-2 left-2 bg-primary text-white px-2 py-1 rounded text-xs font-semibold">
                        {discount}% OFF
                    </div>
                )}

                {/* Wishlist Button */}
                <button
                    onClick={handleWishlistClick}
                    disabled={wishlistLoading}
                    className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-card rounded-full shadow-sm hover:shadow-md transition-shadow disabled:opacity-60"
                >
                    {wishlistLoading ? (
                        <i className="ri-loader-4-line animate-spin text-primary text-sm"></i>
                    ) : (
                        <i className={`${isWishlisted ? 'ri-heart-fill text-primary' : 'ri-heart-line text-muted-foreground'}`}></i>
                    )}
                </button>
            </div>

            {/* Product Info */}
            <div className="p-4 flex flex-col flex-1">
                <h3 className="text-sm font-medium text-foreground mb-2 line-clamp-2 min-h-[40px]">
                    {product.name}
                </h3>

                {/* Pricing */}
                <div className="mb-3">
                    <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                        {product.priceFrom && (
                            <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                                From
                            </span>
                        )}
                        <span className="text-xl font-bold text-foreground">
                            KSh {price.toLocaleString()}
                        </span>
                        {strikePrice && strikePrice > price && (
                            <span className="text-sm text-muted-foreground line-through">
                                KSh {strikePrice.toLocaleString()}
                            </span>
                        )}
                    </div>
                    {shouldShowIncVatLabel(product?.vatMode) && (
                        <p className="text-[11px] text-muted-foreground mb-1">Inc. V.A.T</p>
                    )}
                    {hasOffer && pricing.offer?.name && (
                        <span className="inline-block text-xs bg-primary/10 text-primary px-2 py-0.5 rounded font-medium mb-1">
                            {pricing.offer.name}
                        </span>
                    )}

                    {/* EMI Info */}
                    {product.emi && (
                        <p className="text-xs text-success">
                            EMI from KSh {product.emi.toLocaleString()}/month
                        </p>
                    )}
                </div>

                {/* KCB Offer Badge */}
                {product.hasKCBOffer && (
                    <div className="mb-3">
                        <span className="inline-block bg-[#E8F5E9] text-success text-xs px-2 py-1 rounded font-medium">
                            KCB Offer Available
                        </span>
                    </div>
                )}

                {/* Add to Cart Button */}
                <button
                    onClick={handleAddToCart}
                    disabled={adding || isOutOfStock}
                    className={`w-full text-white py-2.5 rounded-lg font-medium transition-all duration-300 mt-auto whitespace-nowrap ${
                        isOutOfStock
                            ? 'bg-muted-foreground/40 cursor-not-allowed opacity-100 translate-y-0'
                            : 'bg-primary hover:brightness-110 hover:shadow-lg opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0'
                    }`}
                >
                    {isOutOfStock ? 'Out of Stock' : (adding ? 'Adding...' : 'Add to Cart')}
                </button>
            </div>

            {showAccessoriesModal && (
                <div onClick={(e) => e.stopPropagation()}>
                    <AccessoriesModal
                        items={accessoryItems}
                        productName={product.name}
                        onAddToCart={addToCart}
                        onClose={() => setShowAccessoriesModal(false)}
                        onGoToCart={() => { setShowAccessoriesModal(false); navigate('/cart'); }}
                    />
                </div>
            )}
        </div>
    );
}
